import { useState, type MouseEvent } from "react"
import { Localized } from "@fluent/react"
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined"
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined"
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined"
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined"
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined"
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined"
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    LinearProgress,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { LpField } from "../../../../../app/components/LpField"
import { MemberIdentity } from "../../../../../app/components/MemberIdentity"

import type { DocumentWorkflowStageDto } from "../../documentWorkflowApi"
import {
    getStageTypeChipSx,
    stageTypeChipIcons,
    stageTypeLabels,
    stageTypeStyles,
} from "../../DocumentWorkflowConfig"
import {
    formatDateOnly,
    getLatestStageActionForMember,
    getStageUiType,
    isPastDeadline,
    mapRoleFromApi,
    mapStageStatusToUi,
    mapWorkflowActionStatus,
    mapWorkflowActionTypeLabel,
    normalizeWorkflowEnum,
    selectCurrentStage,
} from "../../documentWorkflowHelpers"
import { WorkflowStatusBar } from "./WorkflowStatusBar"
import { DocumentTeamMemberRoleChip } from "../DocumentTeamMemberRoleChip"
import { DocumentWorkflowTimelineDialog } from "../../dialogs/DocumentWorkflowTimelineDialog"

export type CurrentStageOverviewProps = {
    documentId?: number | string | null
    sections: DocumentWorkflowStageDto[]
    currentStageId: number | null
    documentStatus?: string | null
    onAddMember?: (stageId: number) => void
    onRemoveMember?: (stageId: number, documentTeamMemberId: number) => void
    onExtendMemberDeadline?: (stageId: number, documentTeamMemberId: number) => void
    onExtendAllDeadlines?: (stageId: number) => void
    onSendReminderToMember?: (stageId: number, documentTeamMemberId: number) => void
    onSendReminderToAll?: (stageId: number) => void
}

export const CurrentStageOverview = ({
    documentId,
    sections,
    currentStageId,
    documentStatus,
    onAddMember,
    onRemoveMember,
    onExtendMemberDeadline,
    onExtendAllDeadlines,
    onSendReminderToMember,
    onSendReminderToAll,
}: CurrentStageOverviewProps) => {
    const [timelineDialogOpen, setTimelineDialogOpen] = useState(false)
    const [isCollapsed, setIsCollapsed] = useState(false)
    const [memberMenuAnchor, setMemberMenuAnchor] = useState<HTMLElement | null>(null)
    const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null)

    const currentStage = selectCurrentStage(sections, currentStageId)

    if (isCollapsed) {
        return <Box sx={{ mt: 1.25 }}>
            <WorkflowStatusBar
                stages={sections}
                currentStage={currentStage}
                onOpenWorkflow={() => setIsCollapsed(false)}
            />
        </Box>
    }

    const closeMemberMenu = () => {
        setMemberMenuAnchor(null)
        setSelectedMemberId(null)
    }

    const openMemberMenu = (event: MouseEvent<HTMLElement>, memberId: number) => {
        setMemberMenuAnchor(event.currentTarget)
        setSelectedMemberId(memberId)
    }

    const formatDateTimeValue = (value?: string | null) => {
        if (!value) {
            return "-"
        }

        const date = new Date(value)

        if (Number.isNaN(date.getTime())) {
            return "-"
        }

        return date.toLocaleString("pl-PL")
    }

    const sortedActions = sections
        .flatMap(section => section.actions.map(action => ({
            stageName: section.name,
            action,
        })))
        .sort((left, right) => {
            const leftDate = new Date(left.action.execDate ?? left.action.createDate).getTime()
            const rightDate = new Date(right.action.execDate ?? right.action.createDate).getTime()

            if (Number.isNaN(leftDate) || Number.isNaN(rightDate)) {
                return 0
            }

            return rightDate - leftDate
        })

    const filteredActions = sortedActions.filter(({ action }) => {
        const normalizedActionType = normalizeWorkflowEnum(String(action.actionType))

        return normalizedActionType === "APPROVE"
            || normalizedActionType === "REJECT"
            || normalizedActionType === "FINISHOPINION"
    })

    const actionsOverviewPanel = <Paper variant="outlined" sx={{ mt: 1.25, overflow: "hidden" }}>
        <Box
            sx={{
                px: 1.5,
                py: 1.1,
                bgcolor: "action.hover",
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", md: "row" },
                gap: 0.5,
            }}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                <Localized id="document-workflow-approvals-card-title">
                    <span>_Karta zatwierdzen</span>
                </Localized>
            </Typography>
        </Box>

        {filteredActions.length === 0
            ? <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 2 }}>
                <Localized id="document-workflow-approvals-card-empty">
                    <span>_Karta zatwierdzen pusta</span>
                </Localized>
            </Typography>
            : <Stack divider={<Divider flexItem />}>
                {filteredActions.map(({ stageName, action }, index) => {
                    const isApproveAction = normalizeWorkflowEnum(String(action.actionType)) === "APPROVE"
                    const normalizedActionResult = String(action.actionResult ?? "").trim().toUpperCase()
                    const isRejectedApproval = isApproveAction && normalizedActionResult === "R"
                    const isAcceptedApproval = isApproveAction && normalizedActionResult === "A"
                    const statusInfo = isRejectedApproval
                        ? mapWorkflowActionStatus("REJECTED")
                        : isAcceptedApproval
                            ? {
                                ...mapWorkflowActionStatus("APPROVED"),
                                label: "ZATWIERDZONO",
                            }
                            : mapWorkflowActionStatus(action.status)
                    const approvalResult = isRejectedApproval
                        ? "rejected"
                        : isAcceptedApproval
                            ? "approved"
                            : null
                    const eventDate = action.execDate ?? action.createDate

                    return <Box
                        key={`${stageName}-${action.id}`}
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "auto minmax(0, 1fr)",
                                md: "auto minmax(240px, 1fr) minmax(180px, 0.7fr) auto",
                            },
                            alignItems: "center",
                            gap: { xs: 0.75, md: 2 },
                            px: 1.5,
                            py: 1.1,
                        }}
                    >
                        <LpField value={index + 1} sx={{ mt: { xs: 0.15, md: 0 } }} />

                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 650 }} noWrap>
                                {action.fullName || action.userName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }} noWrap>
                                <Localized id="document-workflow-stage-label">
                                    <span>_Etap</span>
                                </Localized>
                                {": "}
                                <Box component="span" sx={{ fontWeight: 700,  color: "text.primary" }}>
                                    {stageName}
                                </Box>
                            </Typography>
                        </Box>

                        <Box sx={{ minWidth: 0, gridColumn: { xs: "2 / 3", md: "auto" } }}>
                            <Typography variant="body2">
                                {approvalResult
                                    ? <Localized id={`document-workflow-action-${approvalResult}`}>
                                        <span>{approvalResult === "approved" ? "Document approved" : "Document rejected"}</span>
                                    </Localized>
                                    : mapWorkflowActionTypeLabel(action.actionType)}
                            </Typography>
                            {action.comment && <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                {action.comment}
                            </Typography>}
                        </Box>

                        <Box sx={{ display: "flex", alignItems: { xs: "flex-start", md: "flex-end" }, flexDirection: "column", gap: 0.35, gridColumn: { xs: "2 / 3", md: "auto" } }}>
                            <Chip
                                label={approvalResult
                                    ? <Localized id={`document-workflow-action-status-${approvalResult}`}>
                                        <span>{approvalResult === "approved" ? "APPROVED" : "REJECTED"}</span>
                                    </Localized>
                                    : statusInfo.label}
                                size="small"
                                color={statusInfo.color}
                                variant="outlined"
                            />
                            <Typography variant="caption" color="text.secondary">
                                {formatDateOnly(eventDate)}
                            </Typography>
                        </Box>
                    </Box>
                })}
            </Stack>}
    </Paper>

    const historyIconButton = <Tooltip title="Pokaz pelna historie">
        <span>
            <IconButton
                size="small"
                color={timelineDialogOpen ? "primary" : "default"}
                disabled={!documentId}
                onClick={() => setTimelineDialogOpen(true)}
                aria-label="Pokaz pelna historie"
            >
                <HistoryOutlinedIcon fontSize="small" />
            </IconButton>
        </span>
    </Tooltip>

    const collapseOverviewButton = <Tooltip title="Zwin podsumowanie etapu">
        <IconButton
            size="small"
            onClick={() => {
                setTimelineDialogOpen(false)
                closeMemberMenu()
                setIsCollapsed(true)
            }}
            aria-label="Zwin podsumowanie etapu"
        >
            <KeyboardArrowUpRoundedIcon fontSize="small" />
        </IconButton>
    </Tooltip>

    const isWorkflowCompleted = sections.length > 0
        && currentStage === null
        && sections.every(section => mapStageStatusToUi(section.status).kind === "completed")

    const isReviewedWaitingForNextStage = documentStatus === "D"

    if (isWorkflowCompleted) {
        return <Box sx={{ mt: 1.25 }}>
            <Paper
                variant="outlined"
                sx={(theme) => ({
                    p: 1.5,
                    borderColor: alpha(theme.palette.success.main, 0.35),
                    bgcolor: alpha(theme.palette.success.main, 0.045),
                })}
            >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CheckCircleOutlineIcon color="success" />
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 750, lineHeight: 1.25 }}>
                                Workflow zakonczony
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Wszystkie etapy opiniowania i zatwierdzania zostaly zakonczone.
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                        {historyIconButton}
                        {collapseOverviewButton}
                    </Box>
                </Box>
            </Paper>
            {actionsOverviewPanel}
        </Box>
    }

    if (!currentStage) {
        return <Box sx={{ mt: 1.25 }}>
            <Paper
                variant="outlined"
                sx={(theme) => ({
                    p: 1.5,
                    borderColor: isReviewedWaitingForNextStage
                        ? alpha(theme.palette.info.main, 0.35)
                        : undefined,
                    bgcolor: isReviewedWaitingForNextStage
                        ? alpha(theme.palette.info.main, 0.045)
                        : undefined,
                })}
            >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <HourglassTopOutlinedIcon
                            color={isReviewedWaitingForNextStage ? "info" : "action"}
                        />
                        <Box>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 750,
                                    lineHeight: 1.25,
                                    color: isReviewedWaitingForNextStage
                                        ? "info.main"
                                        : "text.primary",
                                }}
                            >
                                {isReviewedWaitingForNextStage
                                    ? "Dokument zaopiniowany"
                                    : "Workflow nie jest aktywny"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Dokument oczekuje na uruchomienie kolejnego etapu.
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                        {historyIconButton}
                        {collapseOverviewButton}
                    </Box>
                </Box>
            </Paper>
            {actionsOverviewPanel}
        </Box>
    }

    const stageType = getStageUiType(currentStage)
    const stageStyle = stageTypeStyles[stageType]
    const currentStageIndex = sections.findIndex(section => section.id === currentStage.id)
    const isInitialAuthorOnlyStage = documentStatus === "N"
        && stageType === "EDIT"
        && currentStage.members.length > 0
        && currentStage.members.every(member => mapRoleFromApi(member.role, "EDIT") === "Autor")

    const membersWithStatus = currentStage.members.map(member => {
        const latestAction = getLatestStageActionForMember(
            currentStage,
            member.documentTeamMemberId,
            member.userName
        )
        const isApproveAction = normalizeWorkflowEnum(String(member.actionType)) === "APPROVE"
        const normalizedActionResult = String(member.actionResult ?? "").trim().toUpperCase()
        const approvalResult = isApproveAction && normalizedActionResult === "A"
            ? "approved"
            : isApproveAction && normalizedActionResult === "R"
                ? "rejected"
                : null
        const statusInfo = approvalResult === "approved"
            ? mapWorkflowActionStatus("APPROVED")
            : approvalResult === "rejected"
                ? mapWorkflowActionStatus("REJECTED")
                : mapWorkflowActionStatus(member.actionStatus)
        const actionStatusInfo = mapWorkflowActionStatus(member.actionStatus)

        return {
            member,
            statusInfo,
            actionStatusInfo,
            approvalResult,
            actionComment: latestAction?.comment?.trim() || null,
        }
    })

    const waitingMembers = membersWithStatus.filter(item => item.actionStatusInfo.waiting)
    const completedMembers = membersWithStatus.filter(item => item.actionStatusInfo.kind === "completed")
    const hasRejectedCompletedAction = membersWithStatus.some(item =>
        item.actionStatusInfo.kind === "completed" && item.approvalResult === "rejected")
    const overdueMembers = waitingMembers.filter(item => isPastDeadline(item.member.deadline))
    const progress = currentStage.members.length > 0
        ? Math.round((completedMembers.length / currentStage.members.length) * 100)
        : 0

    const nearestDeadline = waitingMembers
        .map(item => item.member.deadline)
        .filter((deadline): deadline is string => Boolean(deadline))
        .sort((left, right) => new Date(left).getTime() - new Date(right).getTime())[0]

    const sortedMembers = [...membersWithStatus].sort((left, right) => {
        const leftIsAuthor = mapRoleFromApi(left.member.role, stageType) === "Autor"
        const rightIsAuthor = mapRoleFromApi(right.member.role, stageType) === "Autor"

        if (leftIsAuthor !== rightIsAuthor) {
            return leftIsAuthor ? -1 : 1
        }

        const leftOverdue = left.actionStatusInfo.waiting && isPastDeadline(left.member.deadline)
        const rightOverdue = right.actionStatusInfo.waiting && isPastDeadline(right.member.deadline)

        if (leftOverdue !== rightOverdue) {
            return leftOverdue ? -1 : 1
        }
        if (left.actionStatusInfo.waiting !== right.actionStatusInfo.waiting) {
            return left.actionStatusInfo.waiting ? -1 : 1
        }

        const leftDate = new Date(left.member.deadline ?? left.member.execDate ?? 0).getTime()
        const rightDate = new Date(right.member.deadline ?? right.member.execDate ?? 0).getTime()
        return leftDate - rightDate
    })

    const selectedMember = currentStage.members.find(member => member.documentTeamMemberId === selectedMemberId)
    const canManageSelectedMember = Boolean(
        selectedMember
        && mapWorkflowActionStatus(selectedMember.actionStatus).waiting
    )

    return <Box sx={{ mt: 1.25 }}>
        <Paper
            variant="outlined"
            sx={{
                overflow: "hidden",
                borderColor: hasRejectedCompletedAction
                    ? "error.main"
                    : alpha(stageStyle.color, 0.35),
                bgcolor: hasRejectedCompletedAction
                    ? alpha("#D32F2F", 0.035)
                    : "background.paper",
                boxShadow: `0 8px 28px ${alpha(hasRejectedCompletedAction ? "#D32F2F" : stageStyle.color, 0.08)}`,
            }}
        >
            <Box
                sx={{
                    px: { xs: 1.5, md: 2 },
                    py: 1.5,
                    background: hasRejectedCompletedAction
                        ? `linear-gradient(135deg, ${alpha("#D32F2F", 0.1)} 0%, ${alpha("#D32F2F", 0.035)} 100%)`
                        : `linear-gradient(135deg, ${alpha(stageStyle.color, 0.105)} 0%, ${alpha(stageStyle.color, 0.025)} 100%)`,
                    borderBottom: "1px solid",
                    borderColor: alpha(hasRejectedCompletedAction ? "#D32F2F" : stageStyle.color, 0.22),
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: { xs: "flex-start", lg: "center" },
                        justifyContent: "space-between",
                        flexDirection: { xs: "column", lg: "row" },
                        gap: 1.5,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, minWidth: 0 }}>
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                flexShrink: 0,
                                display: "grid",
                                placeItems: "center",
                                borderRadius: 1.5,
                                color: stageStyle.color,
                                bgcolor: alpha(stageStyle.color, 0.12),
                                border: "1px solid",
                                borderColor: alpha(stageStyle.color, 0.25),
                                "& svg": { fontSize: 23 },
                            }}
                        >
                            {stageTypeChipIcons[stageType]}
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                                <Localized
                                    id="document-workflow-current-stage-current-step"
                                    vars={{
                                        current: currentStageIndex + 1,
                                        total: sections.length,
                                    }}
                                >
                                    <Typography variant="overline" sx={{ color: stageStyle.color, fontWeight: 800, lineHeight: 1.2 }}>
                                        _Biezacy etap {currentStageIndex + 1} z {sections.length}
                                    </Typography>
                                </Localized>
                                <Chip
                                    size="small"
                                    label={stageTypeLabels[stageType]}
                                    variant="outlined"
                                    sx={{ ...getStageTypeChipSx(stageType, true), height: 22 }}
                                />
                            </Box>
                            <Typography variant="h6" sx={{ mt: 0.25, fontWeight: 750, lineHeight: 1.25 }}>
                                {isInitialAuthorOnlyStage ? "Opracowanie dokumentu" : currentStage.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                {isInitialAuthorOnlyStage
                                    ? "Etap opiniowania i zatwierdzania zostanie uruchomiony po opracowaniu dokumentu przez autora."
                                    : currentStage.description}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                        {!isInitialAuthorOnlyStage && <Button
                            size="small"
                            variant="contained"
                            startIcon={<PersonAddAltOutlinedIcon />}
                            disabled={!onAddMember}
                            onClick={() => onAddMember?.(currentStage.id)}
                        >
                            <Localized id="document-workflow-current-stage-add-person">
                                <span>_Dodaj osobę</span>
                            </Localized>
                        </Button>}

                        {!isInitialAuthorOnlyStage && <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EventRepeatOutlinedIcon />}
                            disabled={waitingMembers.length === 0 || !onExtendAllDeadlines}
                            onClick={() => onExtendAllDeadlines?.(currentStage.id)}
                        >
                            <Localized id="deadline">
                                <span>_Termin</span>
                            </Localized>
                        </Button>}

                        {!isInitialAuthorOnlyStage && <Button
                            size="small"
                            variant="outlined"
                            startIcon={<NotificationsActiveOutlinedIcon />}
                            disabled={waitingMembers.length === 0 || !onSendReminderToAll}
                            onClick={() => onSendReminderToAll?.(currentStage.id)}
                        >
                            Przypomnij
                        </Button>}

                        {historyIconButton}
                        {collapseOverviewButton}
                    </Box>
                </Box>

                {hasRejectedCompletedAction && <Alert
                    severity="error"
                    variant="filled"
                    sx={{ mt: 1.5, py: 0.25, fontWeight: 700 }}
                >
                    <Localized id="document-workflow-action-rejected">
                        <span>Document rejected</span>
                    </Localized>
                </Alert>}

                {!isInitialAuthorOnlyStage && <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
                        gap: 1,
                        mt: 1.5,
                    }}
                >
                    <Box sx={{ p: 1, borderRadius: 1.25, bgcolor: alpha("#FFFFFF", 0.72), border: "1px solid", borderColor: "divider" }}>
                        <Localized id="document-workflow-current-stage-progress">
                            <Typography variant="caption" color="text.secondary">_Postep etapu</Typography>
                        </Localized>
                        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mt: 0.25 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 750 }}>
                                {completedMembers.length} z {currentStage.members.length}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{progress}%</Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{ mt: 0.5, height: 6, borderRadius: 999, bgcolor: alpha(stageStyle.color, 0.12), "& .MuiLinearProgress-bar": { bgcolor: stageStyle.color } }}
                        />
                    </Box>

                    <Box sx={{ p: 1, borderRadius: 1.25, bgcolor: alpha("#FFFFFF", 0.72), border: "1px solid", borderColor: overdueMembers.length > 0 ? "error.light" : "divider" }}>
                        <Localized id="document-workflow-current-stage-waiting-for">
                            <Typography variant="caption" color="text.secondary">_Oczekujemy na</Typography>
                        </Localized>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.35 }}>
                            <HourglassTopOutlinedIcon sx={{ fontSize: 18, color: overdueMembers.length > 0 ? "error.main" : "warning.main" }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 750 }}>
                                {waitingMembers.length} {waitingMembers.length === 1 ? "osobe" : "osob"}
                            </Typography>
                            {overdueMembers.length > 0 && <Chip size="small" color="error" label={`${overdueMembers.length} po terminie`} sx={{ height: 22 }} />}
                        </Box>
                    </Box>

                    <Box sx={{ p: 1, borderRadius: 1.25, bgcolor: alpha("#FFFFFF", 0.72), border: "1px solid", borderColor: "divider" }}>
                        <Localized id="document-workflow-current-stage-nearest-deadline">
                            <Typography variant="caption" color="text.secondary">_Najblizszy termin</Typography>
                        </Localized>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.35 }}>
                            <AccessTimeOutlinedIcon sx={{ fontSize: 18, color: nearestDeadline && isPastDeadline(nearestDeadline) ? "error.main" : "text.secondary" }} />
                            <Typography
                                variant="subtitle1"
                                color={nearestDeadline && isPastDeadline(nearestDeadline) ? "error.main" : "text.primary"}
                                sx={{ fontWeight: 750 }}
                            >
                                {nearestDeadline ? formatDateOnly(nearestDeadline) : "Brak terminu"}
                            </Typography>
                        </Box>
                    </Box>
                </Box>}
            </Box>

            {!isInitialAuthorOnlyStage && <Box>
                {sortedMembers.length === 0
                    ? <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
                        Brak osób przypisanych do bieżącego etapu.
                    </Typography>
                    : <Stack divider={<Divider flexItem />}>
                        {sortedMembers.map(({ member, statusInfo, actionStatusInfo, approvalResult, actionComment }) => {
                            const overdue = actionStatusInfo.waiting && isPastDeadline(member.deadline)
                            const isCompleted = statusInfo.color === "success"
                            const statusDateLabel = actionStatusInfo.waiting
                                ? member.deadline
                                    ? `${overdue ? "Termin minal" : "Termin"}: ${formatDateOnly(member.deadline)}`
                                    : "Nie ustawiono terminu"
                                : member.execDate
                                    ? `Wykonano: ${formatDateTimeValue(member.execDate)}`
                                    : "Brak daty wykonania"

                            return <Box
                                key={member.documentTeamMemberId}
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr auto", md: "minmax(260px, 1fr) minmax(190px, auto) 40px" },
                                    alignItems: "center",
                                    columnGap: 1.5,
                                    rowGap: 0.75,
                                    px: { xs: 1.5, md: 2 },
                                    py: 1.15,
                                    bgcolor: overdue ? alpha("#D32F2F", 0.025) : "transparent",
                                    "&:hover": { bgcolor: overdue ? alpha("#D32F2F", 0.05) : "action.hover" },
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.15, minWidth: 0 }}>
                                    <MemberIdentity
                                        name={member.fullName}
                                        userName={member.userName}
                                        position={member.position}
                                        companyName={member.companyName}
                                        size="medium"
                                        avatarColor={stageStyle.color}
                                        avatarBackgroundColor={alpha(stageStyle.color, 0.13)}
                                    />
                                    <DocumentTeamMemberRoleChip
                                        role={member.role}
                                        stageType={stageType}
                                    />
                                </Box>

                                <Box sx={{ gridColumn: { xs: "1 / 2", md: "auto" }, pl: { xs: 6.55, md: 0 } }}>
                                    <Chip
                                        size="small"
                                        icon={isCompleted ? <CheckCircleOutlineIcon /> : <HourglassTopOutlinedIcon />}
                                        label={overdue
                                            ? "PO TERMINIE"
                                            : approvalResult
                                                ? <Localized id={`document-workflow-action-status-${approvalResult}`}>
                                                    <span>{approvalResult === "approved" ? "APPROVED" : "REJECTED"}</span>
                                                </Localized>
                                                : statusInfo.label}
                                        color={overdue ? "error" : statusInfo.color}
                                        variant={statusInfo.waiting ? "filled" : "outlined"}
                                        sx={{ fontWeight: 700, "& .MuiChip-icon": { color: "inherit" } }}
                                    />
                                    <Typography
                                        variant="caption"
                                        color={overdue ? "error.main" : "text.secondary"}
                                        sx={{ display: "block", mt: 0.35, fontWeight: overdue ? 650 : 400 }}
                                    >
                                        {statusDateLabel}
                                    </Typography>
                                    {actionComment && <Typography
                                        variant="caption"
                                        color={approvalResult === "rejected" ? "error.main" : "text.secondary"}
                                        sx={{
                                            display: "block",
                                            mt: 0.35,
                                            whiteSpace: "pre-wrap",
                                            overflowWrap: "anywhere",
                                        }}
                                    >
                                        {actionComment}
                                    </Typography>}
                                </Box>

                                <Tooltip title="Akcje osoby">
                                    <IconButton
                                        size="small"
                                        onClick={event => openMemberMenu(event, member.documentTeamMemberId)}
                                        aria-label={`Akcje osoby ${member.fullName}`}
                                    >
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        })}
                    </Stack>}
            </Box>}
        </Paper>

        <Menu
            anchorEl={memberMenuAnchor}
            open={Boolean(memberMenuAnchor)}
            onClose={closeMemberMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
            <MenuItem
                disabled={!canManageSelectedMember || !onExtendMemberDeadline}
                onClick={() => {
                    if (selectedMemberId !== null) {
                        onExtendMemberDeadline?.(currentStage.id, selectedMemberId)
                    }
                    closeMemberMenu()
                }}
            >
                <EventRepeatOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                Termin
            </MenuItem>
            <MenuItem
                disabled={!canManageSelectedMember || !onSendReminderToMember}
                onClick={() => {
                    if (selectedMemberId !== null) {
                        onSendReminderToMember?.(currentStage.id, selectedMemberId)
                    }
                    closeMemberMenu()
                }}
            >
                <NotificationsActiveOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                Przypomnij
            </MenuItem>
            <Divider />
            <MenuItem
                disabled={!canManageSelectedMember || !onRemoveMember}
                onClick={() => {
                    if (selectedMemberId !== null) {
                        onRemoveMember?.(currentStage.id, selectedMemberId)
                    }
                    closeMemberMenu()
                }}
                sx={{ color: "error.main" }}
            >
                <PersonRemoveOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                Usun z etapu
            </MenuItem>
        </Menu>

        <DocumentWorkflowTimelineDialog
            open={timelineDialogOpen}
            documentId={documentId !== null && documentId !== undefined ? String(documentId) : undefined}
            onClose={() => setTimelineDialogOpen(false)}
        />

        {actionsOverviewPanel}
    </Box>
}